import { getPromotionDataFormatConfig } from '@bingads-webui-campaign/promotion-extension';
import { convertCurrencyCode } from '@bingads-webui-campaign-legacy/adextension-price';
import { UAPAdExtensionType as EAdExtensionType } from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { readExtensionAssociations } from '@bingads-webui-campaign-react/perf-max-data';

import { SupportedExtensionPreviewTypes } from '../common/constants';


export const promotionFormatter = (i18n, promotion) => {
  const {
    OccasionI18nKeys,
    ItemPreviewI18nKeys,
    getPromotionTypeForModel,
    getPromotionDetailTypeForModel,
    Occasion,
    PromotionItem,
  } = getPromotionDataFormatConfig(promotion);

  const promotionType = getPromotionTypeForModel(promotion);
  let promotionTypeI18nKey;
  let promotionTypeI18nData;
  switch (promotionType) {
    case 'UptoMonetaryDiscount':
      promotionTypeI18nKey = ItemPreviewI18nKeys['Up to {{monetaryDiscount}} off'];
      promotionTypeI18nData = { monetaryDiscount: i18n.formatCurrency(promotion.MoneyAmountOff, promotion.CurrencyCode) };
      break;
    case 'MonetaryDiscount':
      promotionTypeI18nKey = ItemPreviewI18nKeys['{{monetaryDiscount}} off'];
      promotionTypeI18nData = { monetaryDiscount: i18n.formatCurrency(promotion.MoneyAmountOff, promotion.CurrencyCode) };
      break;
    case 'UptoPercentDiscount':
      promotionTypeI18nKey = ItemPreviewI18nKeys['Up to {{percentDiscount}} off'];
      promotionTypeI18nData = { percentDiscount: i18n.formatPercent(promotion.PercentOff / 100) };
      break;
    case 'PercentDiscount':
      promotionTypeI18nKey = ItemPreviewI18nKeys['{{percentDiscount}} off'];
      promotionTypeI18nData = { percentDiscount: i18n.formatPercent(promotion.PercentOff / 100) };
      break;
    default:
      promotionTypeI18nKey = '';
      promotionTypeI18nData = {};
  }
  const promotionTypeFormatted = i18n.getString(promotionTypeI18nKey, promotionTypeI18nData);

  const promotionDetail = getPromotionDetailTypeForModel(promotion);
  let promotionDetailI18nKey = '';
  let promotionDetailI18nData;
  let promotionDetailFormatted = '';
  const hasPromotionDetail = promotionDetail !== 'None';
  if (hasPromotionDetail) {
    switch (promotionDetail) {
      case 'OnOrdersOver':
        promotionDetailI18nKey = ItemPreviewI18nKeys['On orders over {{amount}}'];
        promotionDetailI18nData = { amount: i18n.formatCurrency(promotion.OrdersOverAmount, promotion.CurrencyCode) };
        break;
      case 'PromotionCode':
        promotionDetailI18nKey = ItemPreviewI18nKeys['Code {{promotionCode}}'];
        promotionDetailI18nData = { promotionCode: promotion.PromotionCode };
        break;
      default:
        promotionDetailI18nKey = '';
        promotionDetailI18nData = {};
    }
    promotionDetailFormatted = i18n.getString(promotionDetailI18nKey, promotionDetailI18nData);
  }

  let dateRangeI18nKey = '';
  let dateRangeI18nData;
  let startDateFormatted = '';
  let endDateFormatted = '';
  function formatISODateString(isoDatetimeString) {
    const dateToUse = i18n.parseDate(
      isoDatetimeString.substring(0, 10),
      {
        raw: 'yyyy-MM-dd',
        timeZone: null,
      }
    );

    return i18n.formatDate(dateToUse, { skeleton: 'MMMd', timeZone: null });
  }

  let dateRangeFormatted = '';
  const hasDateRange = promotion.PromotionStartDate || promotion.PromotionEndDate;
  if (hasDateRange) {
    if (promotion.PromotionStartDate) {
      startDateFormatted = formatISODateString(promotion.PromotionStartDate);
    }
    if (promotion.PromotionEndDate) {
      endDateFormatted = formatISODateString(promotion.PromotionEndDate);
    }
    if (startDateFormatted && endDateFormatted) {
      if (startDateFormatted !== endDateFormatted) {
        dateRangeI18nKey = ItemPreviewI18nKeys['Valid {{startDate}} - {{endDate}}'];
        dateRangeI18nData = { startDate: startDateFormatted, endDate: endDateFormatted };
      } else {
        dateRangeI18nKey = ItemPreviewI18nKeys['Valid {{date}}'];
        dateRangeI18nData = { date: startDateFormatted };
      }
    } else if (startDateFormatted) {
      dateRangeI18nKey = ItemPreviewI18nKeys['Starts {{startDate}}'];
      dateRangeI18nData = { startDate: startDateFormatted };
    } else if (endDateFormatted) {
      dateRangeI18nKey = ItemPreviewI18nKeys['Ends {{endDate}}'];
      dateRangeI18nData = { endDate: endDateFormatted };
    }
    dateRangeFormatted = i18n.getString(dateRangeI18nKey, dateRangeI18nData);
  }

  return {
    Occasion: i18n.getString(OccasionI18nKeys[Occasion] || Occasion),
    PromotionType: promotionTypeFormatted,
    PromotionDetail: promotionDetailFormatted,
    PromotionItem,
    DateRange: dateRangeFormatted,
    hasOccasion: Occasion !== 'None',
    hasPromotionDetail,
    hasDateRange,
  };
};

const priceExtensionFormatter = price => ({
  ...price,
  Currency: convertCurrencyCode(price.CurrencyCode),
});

export const getExtensionFormatters = i18n => ({
  [EAdExtensionType.Promotion]: promotion => promotionFormatter(i18n, promotion),
  [EAdExtensionType.Price]: price => priceExtensionFormatter(price),
});

const getExtensionsFromResponse = (response) => {
  const { value } = response;
  return value.map(({ AdExtension }) => ({
    ...AdExtension,
  }));
};

export const loadAdExtensionData = (cid, aid, campaignId) => {
  const payload = {
    gridDataParameters: {
      DateRange: {
        // Just want to get extension data and don't care BI data, so pass dummy date ranges.
        StartDate: '2024-05-05',
        EndDate: '2024-05-05',
      },
    },
    associationType: 'Campaign',
  };

  return Promise.all([
    readExtensionAssociations(cid, aid, campaignId, { ...payload, adExtensionTypes: [SupportedExtensionPreviewTypes.Sitelink] }, 4),
    readExtensionAssociations(cid, aid, campaignId, { ...payload, adExtensionTypes: [SupportedExtensionPreviewTypes.Callout] }, 4),
    readExtensionAssociations(cid, aid, campaignId, { ...payload, adExtensionTypes: [SupportedExtensionPreviewTypes.Promotion] }, 1),
    readExtensionAssociations(cid, aid, campaignId, { ...payload, adExtensionTypes: [SupportedExtensionPreviewTypes.StructuredSnippet] }, 1),
    readExtensionAssociations(cid, aid, campaignId, { ...payload, adExtensionTypes: [SupportedExtensionPreviewTypes.Price] }, 8),
  ]).then(([sitelinkExtensions, calloutExtensions, promotionExtensions, structuredSnippetExtensions, priceExtensions]) => ({
    SiteLink: getExtensionsFromResponse(sitelinkExtensions),
    Callout: getExtensionsFromResponse(calloutExtensions),
    Promotion: getExtensionsFromResponse(promotionExtensions),
    StructuredSnippet: getExtensionsFromResponse(structuredSnippetExtensions),
    Price: getExtensionsFromResponse(priceExtensions),
  }));
};
